import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Icon, RiskPill, StatusBadge, ToolLogo } from '../components/atoms';
import { formatRelative } from '../lib/format';
import { CATEGORIES } from '@air/shared';
import type { Category, RegistryRow, Risk } from '@air/shared';

type Bucket = 'approved' | 'pending' | 'rejected' | 'all';

const TABS: Array<{ id: Bucket; label: string }> = [
  { id: 'approved', label: 'Approved' },
  { id: 'pending', label: 'Pending Review' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

export function RegistryPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  // When the user is actively searching, default to "all" so results aren't
  // silently filtered by status. They can still narrow with the tabs.
  const bucket: Bucket =
    (params.get('status') as Bucket | null) ?? (q ? 'all' : 'approved');
  const category = (params.get('category') as Category | null) ?? null;
  const risk = (params.get('risk') as Risk | null) ?? null;
  const [search, setSearch] = useState(q);
  const [view, setView] = useState<'cards' | 'list'>('cards');

  const counts = useQuery({
    queryKey: ['registry-counts'],
    queryFn: api.registryCounts,
    staleTime: 30_000,
  });

  const list = useQuery({
    queryKey: ['registry', bucket, category, risk, q],
    queryFn: () =>
      api.registry({
        bucket,
        category: category ?? undefined,
        risk: risk ?? undefined,
        q: q || undefined,
        pageSize: 200,
      }),
  });

  function updateParam(key: string, value: string | null): void {
    const p = new URLSearchParams(params);
    if (value === null || value === '') p.delete(key);
    else p.set(key, value);
    setParams(p);
  }

  function clearAllFilters(): void {
    setSearch('');
    setParams(new URLSearchParams());
  }

  return (
    <div className="reveal">
      <div className="page-headbar">
        <div>
          <div className="page-overline">Approved Software Registry</div>
          <h1 className="page-title">Browse every AI tool reviewed at VIN</h1>
          <p className="page-desc">
            Search before you submit — most popular tools are already approved. Click any tool to
            see its review history, conditions, and submitter.
          </p>
        </div>
      </div>

      <div className="toolbar reveal reveal-1">
        <div className="search" style={{ maxWidth: 460 }}>
          <Icon name="navigate-arrow" size={12} />
          <input
            placeholder="Search by tool, vendor, category, or ticket #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') updateParam('q', search);
            }}
            onBlur={() => updateParam('q', search)}
          />
        </div>
        <select
          value={category ?? 'all'}
          onChange={(e) => updateParam('category', e.target.value === 'all' ? null : e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={risk ?? 'all'}
          onChange={(e) => updateParam('risk', e.target.value === 'all' ? null : e.target.value)}
          style={selectStyle}
        >
          <option value="all">All Risk Levels</option>
          <option value="low">Low Risk</option>
          <option value="med">Medium Risk</option>
          <option value="high">High Risk</option>
        </select>
        <div style={{ marginLeft: 'auto' }} />
        <div className="viewtoggle">
          <button
            className={view === 'cards' ? 'is-active' : ''}
            onClick={() => setView('cards')}
          >
            <Icon name="add-plus" size={11} /> Cards
          </button>
          <button
            className={view === 'list' ? 'is-active' : ''}
            onClick={() => setView('list')}
          >
            <Icon name="edit-penpaper" size={11} /> List
          </button>
        </div>
      </div>

      <div className="toolbar reveal reveal-2" style={{ marginTop: 4, marginBottom: 22 }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`chip ${bucket === t.id ? 'is-active' : ''}`}
            onClick={() =>
              updateParam('status', t.id === 'approved' && !q ? null : t.id)
            }
          >
            {t.label}
            <span className="chip__count">· {counts.data?.[t.id] ?? '…'}</span>
          </button>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-steel)' }}>
          {list.data && (
            <>
              Showing {list.data.rows.length} of {list.data.total}
            </>
          )}
        </div>
      </div>

      {q && (
        <div
          className="reveal reveal-2"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            background: 'var(--color-light-steel)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 8,
            marginBottom: 18,
            fontSize: 13,
            color: 'var(--color-navy)',
          }}
        >
          <Icon name="navigate-arrow" size={12} />
          <span>
            Searching every field for <strong>"{q}"</strong>
            {list.data && (
              <>
                {' '}
                — {list.data.total} match{list.data.total === 1 ? '' : 'es'} across all statuses
              </>
            )}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => {
              setSearch('');
              const p = new URLSearchParams(params);
              p.delete('q');
              setParams(p);
            }}
          >
            Clear search
          </button>
        </div>
      )}

      {list.isLoading && <p className="muted">Loading…</p>}

      {list.data && list.data.rows.length === 0 && (
        <div
          className="card card--padded reveal reveal-3"
          style={{ textAlign: 'center', padding: '60px 24px' }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--color-navy)', marginBottom: 6 }}>
            No tools match your filters
          </div>
          <p className="muted" style={{ fontSize: 13, maxWidth: '44ch', margin: '0 auto 16px' }}>
            Try clearing filters, or submit a request to bring this tool through review.
          </p>
          <button className="btn btn--primary btn--sm" onClick={clearAllFilters}>
            Clear Filters
          </button>
        </div>
      )}

      {list.data && list.data.rows.length > 0 && view === 'cards' && (
        <div className="regrid reveal reveal-3">
          {list.data.rows.map((r) => (
            <RegistryCard key={r.id} r={r} onOpen={(id) => navigate(`/requests/${id}`)} />
          ))}
        </div>
      )}

      {list.data && list.data.rows.length > 0 && view === 'list' && (
        <div className="table-wrap reveal reveal-3">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Ticket</th>
                <th>Tool</th>
                <th style={{ width: 180 }}>Category</th>
                <th style={{ width: 200 }}>Status</th>
                <th style={{ width: 130 }}>Risk</th>
                <th style={{ width: 140 }}>Last Update</th>
              </tr>
            </thead>
            <tbody>
              {list.data.rows.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/requests/${r.id}`)}>
                  <td className="mono">{r.ticketNumber}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <ToolLogo name={r.softwareName} logoUrl={r.logoUrl} size={28} />
                      <div>
                        <div className="tool-name">{r.softwareName}</div>
                        <div className="tool-sub">{r.vendor ?? '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="muted" style={{ fontSize: 12 }}>{r.category}</span>
                  </td>
                  <td>
                    <StatusBadge status={r.status} />
                  </td>
                  <td>
                    <RiskPill risk={r.risk} />
                  </td>
                  <td>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {formatRelative(r.updatedAt)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--border-subtle)',
  background: 'var(--color-white)',
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--color-navy)',
};

function RegistryCard({ r, onOpen }: Readonly<{ r: RegistryRow; onOpen: (id: string) => void }>) {
  return (
    <div className="regcard" onClick={() => onOpen(r.id)}>
      <div className="regcard__head">
        <ToolLogo name={r.softwareName} logoUrl={r.logoUrl} />
        <StatusBadge status={r.status} />
      </div>
      <div>
        <h3 className="regcard__name">{r.softwareName}</h3>
        <p className="regcard__vendor">{r.vendor ?? r.websiteUrl}</p>
      </div>
      <p className="regcard__desc">{r.description}</p>
      <div className="regcard__tags">
        <span className="regcard__tag">{r.category}</span>
        {r.usesConfidentialData && <span className="regcard__tag">Confidential</span>}
        {r.usesCustomerData && <span className="regcard__tag">Customer Data</span>}
      </div>
      <div className="regcard__meta">
        <span className="regcard__meta-key mono" style={{ fontFamily: 'var(--font-mono)' }}>
          {r.ticketNumber}
        </span>
        <RiskPill risk={r.risk} />
      </div>
    </div>
  );
}
